// ABOUTME: Loads and saves the singleton theme_settings row holding site-wide color and typography tokens.
// ABOUTME: Reads are public; saving requires an admin session (enforced by row level security).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { ThemeSettings, ThemeTokens, ThemeTypography } from '@/lib/theme-css';

const EMPTY: ThemeSettings = { tokens_light: {}, tokens_dark: {}, typography: {} };

export const themeSettingsQueryKey = ['theme-settings'];

export const useThemeSettings = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: themeSettingsQueryKey,
    queryFn: async (): Promise<ThemeSettings> => {
      const { data, error } = await supabase
        .from('theme_settings')
        .select('tokens_light, tokens_dark, typography')
        .eq('id', 1)
        .maybeSingle();
      if (error) throw error;
      if (!data) return EMPTY;
      return {
        tokens_light: (data.tokens_light ?? {}) as ThemeTokens,
        tokens_dark: (data.tokens_dark ?? {}) as ThemeTokens,
        typography: (data.typography ?? {}) as Partial<ThemeTypography>,
      };
    },
    staleTime: 2 * 60 * 1000,
  });

  const save = useMutation({
    mutationFn: async (next: ThemeSettings) => {
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('theme_settings')
        .upsert({
          id: 1,
          tokens_light: next.tokens_light,
          tokens_dark: next.tokens_dark,
          typography: next.typography,
          updated_by: userData.user?.id ?? null,
        });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: themeSettingsQueryKey }),
  });

  return {
    settings: query.data ?? EMPTY,
    loading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
    save,
  };
};
